/**
 * Integrations page — vendor data ingestion management.
 *
 * Two surfaces:
 *  1. Vendor cards — one per configured VendorCredential, showing status,
 *     last sync, schedule. With Configure / Test / Run-now / Delete
 *     actions when the user has the right permissions.
 *  2. Recent runs table — IngestionRun rows with status, timing, and any
 *     error messages.
 *
 * Permission gates:
 *   - view_vendor_credentials: required to reach this page (system_admin)
 *   - manage_vendor_credentials: required to add / configure / delete /
 *     test credentials (system_admin)
 *   - trigger_vendor_ingestion: required to run on demand (system_admin)
 *
 * The frontend gates the UI; the backend gates the API. Both layers
 * matter — frontend hides what users shouldn't see; backend rejects
 * curl bypass attempts.
 */
import React, { useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Plus, RefreshCw } from "lucide-react"

import Layout from "../../components/Layout"
import VendorCard from "../../components/integrations/VendorCard"
import WebsiteScrapeConfigCard from "../../components/integrations/WebsiteScrapeConfigCard"
import VendorCredentialForm from "../../components/integrations/VendorCredentialForm"
import RunHistoryTable from "../../components/integrations/RunHistoryTable"
import { useAuth } from "../../context/AuthContext"
import { useDealerGroup } from "../../context/DealerGroupContext"
import {
  IngestionRun,
  VendorCredential,
  deleteVendorCredential,
  listIngestionRuns,
  listVendorCredentials,
  testVendorCredential,
  triggerIngestionRun,
} from "../../api/integrations"
import { THEME } from "../../config/theme"

const RUNS_LIMIT = 25

type FormState =
  | { mode: "closed" }
  | { mode: "create" }
  | { mode: "edit"; credential: VendorCredential }

type CardBusyAction = "test" | "run" | "delete" | null

const IntegrationsPage: React.FC = () => {
  const { hasPermission, isLoading: authLoading } = useAuth()
  // Integrations is a working surface: "All Groups" (dealerGroupId === null) is
  // a read-only view; adding/managing a credential requires a concrete group.
  const { dealerGroupId } = useDealerGroup()
  const navigate = useNavigate()

  const [credentials, setCredentials] = useState<VendorCredential[]>([])
  const [runs, setRuns] = useState<IngestionRun[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Per-card transient state (test result, busy action). Keyed by credential id.
  const [testResults, setTestResults] = useState<
    Record<number, { success: boolean; message: string } | null>
  >({})
  const [busyByCard, setBusyByCard] = useState<Record<number, CardBusyAction>>({})

  // Form modal state
  const [formState, setFormState] = useState<FormState>({ mode: "closed" })

  // Delete confirmation modal state
  const [deleteTarget, setDeleteTarget] = useState<VendorCredential | null>(null)

  const canManage = hasPermission("manage_vendor_credentials")
  const canTrigger = hasPermission("trigger_vendor_ingestion")

  // Permission gate — redirect away if the user can't view this page.
  useEffect(() => {
    if (authLoading) return
    if (!hasPermission("view_vendor_credentials")) {
      navigate("/dashboard", { replace: true })
    }
  }, [authLoading, hasPermission, navigate])

  const loadData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [creds, recentRuns] = await Promise.all([
        listVendorCredentials(dealerGroupId),
        listIngestionRuns({ limit: RUNS_LIMIT, dealerGroupId }),
      ])
      setCredentials(creds)
      setRuns(recentRuns)
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load integrations")
    } finally {
      setLoading(false)
    }
  }, [dealerGroupId])

  useEffect(() => {
    if (authLoading || !hasPermission("view_vendor_credentials")) return
    loadData()
  }, [authLoading, hasPermission, loadData])

  const setCardBusy = (id: number, action: CardBusyAction) =>
    setBusyByCard((prev) => ({ ...prev, [id]: action }))

  const handleTest = async (credential: VendorCredential) => {
    setCardBusy(credential.id, "test")
    setTestResults((prev) => ({ ...prev, [credential.id]: null }))
    try {
      const result = await testVendorCredential(credential.id)
      const message = result.success
        ? `Connected. Fetched ${result.bytes_fetched ?? 0} bytes${
            result.suggested_filename ? ` (${result.suggested_filename})` : ""
          }.`
        : result.error_message ?? "Test failed (no message)"
      setTestResults((prev) => ({
        ...prev,
        [credential.id]: { success: result.success, message },
      }))
    } catch (e) {
      setTestResults((prev) => ({
        ...prev,
        [credential.id]: {
          success: false,
          message: e instanceof Error ? e.message : "Test failed",
        },
      }))
    } finally {
      setCardBusy(credential.id, null)
    }
  }

  const handleRunNow = async (credential: VendorCredential) => {
    setCardBusy(credential.id, "run")
    try {
      await triggerIngestionRun(credential.id)
      // Re-fetch the runs list to show the new run row
      const recentRuns = await listIngestionRuns({ limit: RUNS_LIMIT, dealerGroupId })
      setRuns(recentRuns)
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to trigger run")
    } finally {
      setCardBusy(credential.id, null)
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    const id = deleteTarget.id
    setCardBusy(id, "delete")
    try {
      await deleteVendorCredential(id)
      setDeleteTarget(null)
      await loadData()
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to delete")
    } finally {
      setCardBusy(id, null)
    }
  }

  const handleSaved = () => {
    loadData()
  }

  return (
    <Layout>
      <div
        style={{
          fontSize: "24px",
          fontWeight: 500,
          letterSpacing: "-0.5px",
          color: THEME.colors.neutral.light.textPrimary,
          marginBottom: "8px",
        }}
      >
        Integrations
      </div>
      <div
        style={{
          fontSize: "13px",
          color: THEME.colors.neutral.light.textSecondary,
          marginBottom: "24px",
        }}
      >
        Vendor data ingestion status and recent run history.
      </div>

      {/* Top action bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          marginBottom: "16px",
        }}
      >
        {canManage && dealerGroupId !== null && (
          <button
            type="button"
            onClick={() => setFormState({ mode: "create" })}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
              fontSize: 14,
              fontWeight: 500,
              borderRadius: THEME.borderRadius.md,
              border: "none",
              backgroundColor: THEME.colors.navy,
              color: THEME.colors.white,
              cursor: "pointer",
              fontFamily: THEME.fonts.primary,
            }}
          >
            <Plus size={14} strokeWidth={2} />
            Add credential
          </button>
        )}
        {canManage && dealerGroupId === null && (
          <span
            style={{
              fontSize: "13px",
              color: THEME.colors.neutral.light.textSecondary,
              fontFamily: THEME.fonts.primary,
            }}
          >
            Select a dealer group from the top bar to add or manage credentials.
          </span>
        )}
        <button
          type="button"
          onClick={() => loadData()}
          disabled={loading}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            fontSize: "12px",
            fontWeight: 500,
            padding: "5px 12px",
            border: `1px solid ${THEME.colors.silver}`,
            borderRadius: THEME.borderRadius.md,
            background: THEME.colors.neutral.light.surface,
            color: THEME.colors.neutral.light.textPrimary,
            fontFamily: THEME.fonts.primary,
            cursor: loading ? "not-allowed" : "pointer",
          }}
        >
          <RefreshCw size={12} strokeWidth={2} />
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {/* Error banner */}
      {error && (
        <div
          style={{
            background: "rgba(220, 38, 38, 0.08)",
            color: THEME.colors.semantic.danger,
            border: "0.5px solid rgba(220, 38, 38, 0.3)",
            padding: "10px 14px",
            borderRadius: THEME.borderRadius.md,
            fontSize: "13px",
            marginBottom: "16px",
          }}
        >
          {error}
        </div>
      )}

      {/* Website scrape config — group-level Dealer.com site, not a credential */}
      {dealerGroupId !== null && (
        <>
          <div
            style={{
              fontSize: THEME.typography.caption.size,
              fontWeight: THEME.typography.caption.weight,
              letterSpacing: THEME.typography.caption.tracking,
              textTransform: "uppercase",
              color: THEME.colors.neutral.light.textSecondary,
              marginBottom: "12px",
            }}
          >
            Website scrape
          </div>
          <div style={{ marginBottom: "32px" }}>
            <WebsiteScrapeConfigCard dealerGroupId={dealerGroupId} canManage={canManage} />
          </div>
        </>
      )}

      {/* Vendor cards section */}
      <div
        style={{
          fontSize: THEME.typography.caption.size,
          fontWeight: THEME.typography.caption.weight,
          letterSpacing: THEME.typography.caption.tracking,
          textTransform: "uppercase",
          color: THEME.colors.neutral.light.textSecondary,
          marginBottom: "12px",
        }}
      >
        Configured vendors
      </div>

      {credentials.length === 0 && !loading && !error ? (
        <div
          style={{
            background: THEME.colors.neutral.light.surface,
            border: `1px solid ${THEME.colors.silver}`,
            borderRadius: THEME.borderRadius.md,
            padding: "24px",
            textAlign: "center",
            color: THEME.colors.neutral.light.textSecondary,
            fontSize: "14px",
            marginBottom: "32px",
          }}
        >
          No vendor credentials configured yet.
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: "16px",
            marginBottom: "32px",
          }}
        >
          {credentials.map((c) => (
            <VendorCard
              key={c.id}
              credential={c}
              canManage={canManage}
              canTrigger={canTrigger}
              onConfigure={() => setFormState({ mode: "edit", credential: c })}
              onTest={() => handleTest(c)}
              onRunNow={() => handleRunNow(c)}
              onDelete={() => setDeleteTarget(c)}
              testResult={testResults[c.id]}
              busyAction={busyByCard[c.id] ?? null}
            />
          ))}
        </div>
      )}

      {/* Run history section */}
      <div
        style={{
          fontSize: THEME.typography.caption.size,
          fontWeight: THEME.typography.caption.weight,
          letterSpacing: THEME.typography.caption.tracking,
          textTransform: "uppercase",
          color: THEME.colors.neutral.light.textSecondary,
          marginBottom: "12px",
          marginTop: "8px",
        }}
      >
        Recent ingestion runs
      </div>

      <RunHistoryTable runs={runs} loading={loading} />

      {/* Form modal */}
      {formState.mode !== "closed" && (
        <VendorCredentialForm
          mode={formState.mode}
          credential={formState.mode === "edit" ? formState.credential : null}
          dealerGroupId={dealerGroupId}
          onClose={() => setFormState({ mode: "closed" })}
          onSaved={handleSaved}
        />
      )}

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setDeleteTarget(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: THEME.colors.neutral.light.surface,
              borderRadius: THEME.borderRadius.lg,
              padding: "24px",
              maxWidth: "440px",
              fontFamily: THEME.fonts.primary,
              color: THEME.colors.neutral.light.textPrimary,
            }}
          >
            <div style={{ fontSize: "18px", fontWeight: 500, marginBottom: "12px" }}>
              Delete credential?
            </div>
            <div style={{ fontSize: "13px", color: THEME.colors.neutral.light.textSecondary, marginBottom: "20px" }}>
              This removes the credential and unschedules any future runs. Existing run history is preserved.
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                style={{
                  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
                  fontSize: 14,
                  fontWeight: 500,
                  borderRadius: THEME.borderRadius.md,
                  border: `1px solid ${THEME.colors.silver}`,
                  backgroundColor: THEME.colors.neutral.light.surface,
                  color: THEME.colors.neutral.light.textPrimary,
                  cursor: "pointer",
                  fontFamily: THEME.fonts.primary,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                style={{
                  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
                  fontSize: 14,
                  fontWeight: 500,
                  borderRadius: THEME.borderRadius.md,
                  border: "none",
                  backgroundColor: THEME.colors.semantic.danger,
                  color: THEME.colors.white,
                  cursor: "pointer",
                  fontFamily: THEME.fonts.primary,
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  )
}

export default IntegrationsPage
